import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, type LayoutChangeEvent } from "react-native";

/**
 * A header whose top part scrolls away while the rest stays pinned over a list, as both public
 * storefronts use it: the shop's identity gives its height back to the cards on the first scroll,
 * and search and filters stay in reach (TRO-905).
 *
 * The header floats over the list (`header.style`, absolutely positioned), the list starts below it
 * (`headerHeight` as its top padding), and the header slides up by the collapsible part's height as
 * the list scrolls. Spread `header` on an `Animated.View`, `collapsible` on the `View` wrapping the
 * part that scrolls away, and `list` on the list; call `reset` when a list remounts at the top.
 *
 * The header slides by the collapsible part's bottom edge less `topInset`, not by its height alone,
 * so the padding above it goes too and no sliver of it (a tab underline) is left showing above the
 * pinned part; the pinned part then sits just below the safe area. On native the status bar is
 * see-through, so a screen also paints `cover` (a `bg-bg` strip the inset's height) over it.
 *
 * With `sticky` on, one row at the top of the collapsible part (the shop's name row) stays on
 * screen instead: spread `sticky` on an `Animated.View` wrapping it. The header then slides by the
 * part's bottom edge less `topInset` less the row's height, so the pinned part sits directly under
 * the row, and the row slides back down by what the header took from it, so it rests under the inset
 * and covers the rest of the collapsible part as that goes by. `collapsed` is true once the header
 * has finished sliding. Off, or not passed, the translation is exactly the one above, and turning it
 * on or off at any scroll offset only changes the translation, never the offset or the layout.
 *
 * NativeWind does not style an `Animated.View`, so the header's look goes on a plain `View` inside.
 */
export function useCollapsingHeader(topInset = 0, sticky = false) {
  const scrollY = useRef(new Animated.Value(0)).current;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [collapsibleHeight, setCollapsibleHeight] = useState(0);
  const [collapsibleTop, setCollapsibleTop] = useState(0);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [pastTravel, setPastTravel] = useState(false);
  const travel = sticky ? Math.max(0, collapsibleHeight - stickyHeight) : collapsibleHeight;
  // The row starts sliding back down once the header has taken the padding above it.
  const rowStart = Math.min(Math.max(0, collapsibleTop - topInset), travel);
  const offset = useRef(0);
  const travelRef = useRef(travel);
  travelRef.current = travel;
  const stickyRef = useRef(sticky);
  stickyRef.current = sticky;
  useEffect(() => {
    const id = scrollY.addListener(({ value }) => {
      offset.current = value;
      setPastTravel(stickyRef.current && travelRef.current > 0 && value >= travelRef.current);
    });
    return () => scrollY.removeListener(id);
  }, [scrollY]);
  useEffect(
    () => setPastTravel(sticky && travel > 0 && offset.current >= travel),
    [sticky, travel],
  );
  // Stable, so a caller can list it in an effect's dependencies.
  const reset = useCallback(() => scrollY.setValue(0), [scrollY]);

  return {
    headerHeight,
    reset,
    collapsed: sticky && pastTravel,
    header: {
      onLayout: (e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height),
      style: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        transform: [{
          translateY: scrollY.interpolate({
            inputRange: [0, Math.max(travel, 1)],
            outputRange: [0, -travel],
            extrapolate: "clamp",
          }),
        }],
      },
    },
    collapsible: {
      onLayout: (e: LayoutChangeEvent) => {
        setCollapsibleTop(e.nativeEvent.layout.y);
        setCollapsibleHeight(
          Math.max(0, e.nativeEvent.layout.y + e.nativeEvent.layout.height - topInset),
        );
      },
    },
    sticky: {
      onLayout: (e: LayoutChangeEvent) => setStickyHeight(e.nativeEvent.layout.height),
      style: {
        zIndex: 1,
        transform: [{
          translateY: scrollY.interpolate({
            inputRange: [0, rowStart, Math.max(travel, rowStart, 1)],
            outputRange: [0, 0, sticky ? travel - rowStart : 0],
            extrapolate: "clamp",
          }),
        }],
      },
    },
    cover: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: topInset,
      zIndex: 11,
    },
    list: {
      onScroll: Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
        useNativeDriver: false,
      }),
      scrollEventThrottle: 16,
      scrollIndicatorInsets: { top: headerHeight },
    },
  };
}
